import {
  base64UrlToBytes,
  bytesToBase64Url,
  bytesToHex,
  concatBytes,
  encodePairingToken,
  encodeQrPairingToken,
  utf8ToBytes,
} from "@zeroclipboard/shared";
import {
  constantTimeEqual,
  hkdfSha256,
  hmacSha256,
  randomBytes,
  sha256,
} from "./primitives";

/** Domain-separated HKDF label binding the link factor and the out-of-band code
 *  into one effective pairing secret. */
const PAIRING_SECRET_INFO = utf8ToBytes("ZeroClipboard/pairing-secret/v1");

/**
 * All material derived from a freshly generated pairing secret.
 *
 * The pairing secret is SPLIT into two independent factors so a leaked link is
 * useless on its own:
 *  - `linkSecret` (128-bit) travels inside the shareable link / QR.
 *  - `code` (6 random digits) is shared out-of-band and required to join via a
 *    link. The QR bundles the code so an in-person scan needs no second factor.
 *
 * Both are combined into the effective secret `secret = S`, which drives the
 * pairing proof, key binding and SAS. Content-encryption keys never use it.
 */
export interface PairingMaterial {
  channelId: string;
  /** 128-bit link factor (carried by the link/QR). */
  linkSecret: Uint8Array;
  linkSecretB64: string;
  /** 6-digit out-of-band factor required to join from a link. */
  code: string;
  /** Effective pairing secret S = HKDF(linkSecret ‖ code). */
  secret: Uint8Array;
  /** Shareable link token (no code): `zc1.<channelId>.<linkSecret>`. */
  token: string;
  /** QR token (bundles the code): `zc1.<channelId>.<linkSecret>.<code>`. */
  qrToken: string;
  pairingSecretHash: string;
  sas: string;
  /** Optional human-verifiable fingerprint of the creator's identity key (§5.1). */
  deviceFingerprint?: string;
}

/** Combine the link factor and the out-of-band code into the effective pairing
 *  secret. Both are required; neither alone reveals it. */
export async function deriveEffectiveSecret(
  channelId: string,
  linkSecret: Uint8Array,
  code: string
): Promise<Uint8Array> {
  const ikm = concatBytes(linkSecret, utf8ToBytes(code));
  return hkdfSha256(ikm, utf8ToBytes(channelId), PAIRING_SECRET_INFO, 32);
}

/** A uniformly-random decimal code, independent of the link secret. */
export function generateNumericCode(digits = 6): string {
  const b = randomBytes(4);
  const n = ((b[0]! << 24) | (b[1]! << 16) | (b[2]! << 8) | b[3]!) >>> 0;
  return (n % 10 ** digits).toString().padStart(digits, "0");
}

/**
 * Short, human-verifiable fingerprint of a device's identity public key
 * (§5.1, optional). Two users can read it aloud to confirm they paired with the
 * intended device — defense in depth on top of the pairing secret. Format:
 * three dash-separated groups of 4 hex chars from SHA-256(publicKey).
 */
export async function deviceFingerprint(
  devicePublicRaw: Uint8Array
): Promise<string> {
  const digest = await sha256(devicePublicRaw);
  const hex = bytesToHex(digest.subarray(0, 6));
  return `${hex.slice(0, 4)}-${hex.slice(4, 8)}-${hex.slice(8, 12)}`;
}

/** Generate a new channel id, split pairing secret, and everything derived.
 *  When the creator's identity public key is supplied, its device fingerprint
 *  (§5.1) is computed and bundled into the QR token. */
export async function createPairingMaterial(
  creatorPublicRaw?: Uint8Array
): Promise<PairingMaterial> {
  const channelId = bytesToBase64Url(randomBytes(16));
  const linkSecret = randomBytes(16); // 128-bit link factor
  const linkSecretB64 = bytesToBase64Url(linkSecret);
  const code = generateNumericCode(6); // independent out-of-band factor
  const secret = await deriveEffectiveSecret(channelId, linkSecret, code);
  const fingerprint = creatorPublicRaw
    ? await deviceFingerprint(creatorPublicRaw)
    : undefined;
  return {
    channelId,
    linkSecret,
    linkSecretB64,
    code,
    secret,
    token: encodePairingToken(channelId, linkSecretB64),
    qrToken: encodeQrPairingToken(channelId, linkSecretB64, code, fingerprint),
    pairingSecretHash: await pairingHash(channelId, secret),
    sas: await sasCode(channelId, secret),
    ...(fingerprint ? { deviceFingerprint: fingerprint } : {}),
  };
}

/** SHA-256(channelId || pairingSecret) — stored by the server, sent as proof. */
export async function pairingHash(
  channelId: string,
  secret: Uint8Array
): Promise<string> {
  const digest = await sha256(concatBytes(utf8ToBytes(channelId), secret));
  return bytesToBase64Url(digest);
}

/** HMAC(pairingSecret, rawDevicePublicKey) — end-to-end key binding. */
export async function keyBinding(
  secret: Uint8Array,
  devicePublicRaw: Uint8Array
): Promise<string> {
  return bytesToBase64Url(await hmacSha256(secret, devicePublicRaw));
}

/**
 * Verify a peer's key binding using the shared pairing secret. Existing members
 * call this to authenticate a joiner's public key WITHOUT trusting the server.
 */
export async function verifyKeyBinding(
  secret: Uint8Array,
  devicePublicRaw: Uint8Array,
  bindingB64: string
): Promise<boolean> {
  const expected = await hmacSha256(secret, devicePublicRaw);
  try {
    const actual = base64UrlToBytes(bindingB64);
    return constantTimeEqual(expected, actual);
  } catch {
    return false;
  }
}

/** Human-verifiable 6-digit short authentication string (SAS). Both devices
 *  display the same value after pairing so users can confirm no MITM. */
export async function sasCode(
  channelId: string,
  secret: Uint8Array
): Promise<string> {
  const mac = await hmacSha256(secret, utf8ToBytes(`SAS|${channelId}`));
  const n = ((mac[0]! << 16) | (mac[1]! << 8) | mac[2]!) % 1_000_000;
  return n.toString().padStart(6, "0");
}
